import { Injectable } from '@angular/core';
import { UserService } from '@features/auth/user.service';
import { CollectionActions } from '@features/collections';
import { SearchActions } from '@features/search/actions/search-actions';
import { StateService } from '@features/state/state.service';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError, map, mergeMap, tap, withLatestFrom,
} from 'rxjs/operators';
import { dismissIntroduction } from '@features/introduction/actions/dismiss-introduction';
import { dig } from '@imcc/utilities';
import {
  loadStateFailure, saveStateFailure, saveStateSuccess, loadStateSuccess,
} from './actions';
import { AppActions } from './app-actions.enum';
import { ClientStateService } from './features/client-state/client-state.service';
import { LocalStorage } from './features/storage/local-storage';
import { LocalStorageAppState } from './local-storage-app-state';

@Injectable()
export class AppEffects {
  constructor(
    private actions$: Actions,
    private stateService: StateService,
    private userService: UserService,
    private clientStateService: ClientStateService,
  ) {}

  @Effect()
  saveStateRequest = this.actions$.pipe(
    ofType(
      CollectionActions.SET_AS_DEFAULT,
      SearchActions.SEARCH_SUCCESS,
      SearchActions.CLOSE_WELCOME,
      dismissIntroduction.type,
    ),
    withLatestFrom(this.userService.user$, this.stateService.state$),
    mergeMap(([, user, { collection, introduction }]) => {
      const saveState = {
        userId: user.sub,
        search: {
          lastUpdated: new Date().getTime(),
        },
        clientCollection: { activeCollectionId: collection.defaultCollectionId },
        introductionDismissedAt: dig(introduction, 'dismissedAt'),
      };

      return this.clientStateService.saveState(saveState).pipe(
        map(() => saveStateSuccess()),
        catchError((error) => of(saveStateFailure({ error }))),
      );
    }),
  );

  @Effect()
  loadStateRequest = this.actions$.pipe(
    ofType(AppActions.LOAD_STATE_REQUEST),
    mergeMap(() => this.clientStateService.loadState().pipe(
      map((state) => loadStateSuccess({ clientStateRequestDTO: state })),
      catchError((error) => of(loadStateFailure({ error }))),
    )),
  );

  @Effect({ dispatch: false })
  persistStateInLocalStorage = this.actions$.pipe(
    withLatestFrom(this.stateService.state$),
    tap(([, state]) => {
      const localStorageClient = new LocalStorage();
      const scrubbedState = new LocalStorageAppState(state).toObject();
      const scrubbedStateJSON = JSON.stringify(scrubbedState);

      localStorageClient.setItem('appState', scrubbedStateJSON);
    }),
  );
}
